import React, { useState, useEffect } from 'react';
import {
  Users, Search, UserCheck, User, ChevronDown, UserPlus, Edit2, Key,
  MoreVertical, CheckCircle, XCircle, Clock, Mail
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { useAdminAuth } from '../context/AdminAuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/utils';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState('all');
  const [search, setSearch] = useState('');

  // Add User modal
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [addUserForm, setAddUserForm] = useState({
    email: '',
    full_name: '',
    role: 'finance_admin',
    department: ''
  });

  const [editForm, setEditForm] = useState({
    full_name: '',
    department: '',
    phone: ''
  });

  const [permissions, setPermissions] = useState({});

  const { api, admin, hasPermission } = useAdminAuth();

  const canInvite = hasPermission('users', 'invite') || hasPermission('team', 'invite');
  const canEdit = hasPermission('users', 'edit') || hasPermission('team', 'edit');
  const availableAdminRoles = roles.filter((role) => {
    const name = (role.name || '').toLowerCase();
    const key = (role.role_key || '').toLowerCase();
    const isMember = name === 'member' || key === 'member';
    const isSuperAdmin = name === 'super admin' || key === 'super_admin';
    if (isMember) return false;
    if (admin?.role !== 'super_admin' && isSuperAdmin) return false;
    return true;
  });

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [userType, search]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (userType !== 'all') params.append('type', userType);
      if (search) params.append('search', search);
      const response = await api.get(`/api/admin/users/?${params.toString()}`);
      setUsers(response.data.users || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get('/api/admin/roles/');
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error('Failed to load roles', error);
    }
  };

  const handleAddUser = async () => {
    if (!addUserForm.email || !addUserForm.full_name) {
      toast.error('Please fill in email and full name');
      return;
    }
    try {
      await api.post('/api/admin/users/invite', addUserForm);
      toast.success('Invitation sent! The user will receive an email to verify and set their password.');
      setShowAddUserDialog(false);
      setAddUserForm({ email: '', full_name: '', role: 'finance_admin', department: '' });
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to add user'));
    }
  };

  const handleUpdate = async () => {
    try {
      await api.put(`/api/admin/team/${selectedUser.user_id}`, editForm);
      toast.success('User updated');
      setShowEditDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to update'));
    }
  };

  const handleUpdatePermissions = async () => {
    try {
      await api.put(`/api/admin/team/${selectedUser.user_id}/permissions`, permissions);
      toast.success('Permissions updated');
      setShowPermissionsDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to update permissions'));
    }
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    try {
      await api.post(`/api/admin/team/${userId}/deactivate`);
      toast.success('User deactivated');
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to deactivate'));
    }
  };

  const handleReactivate = async (userId) => {
    try {
      await api.post(`/api/admin/team/${userId}/reactivate`);
      toast.success('User reactivated');
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to reactivate'));
    }
  };

  const openEditDialog = (u) => {
    setSelectedUser(u);
    setEditForm({
      full_name: u.full_name || '',
      department: u.department || '',
      phone: u.phone || ''
    });
    setShowEditDialog(true);
  };

  const openPermissionsDialog = (u) => {
    setSelectedUser(u);
    setPermissions(u.permissions || {});
    setShowPermissionsDialog(true);
  };

  const handleRoleChange = async (userId, roleId) => {
    try {
      await api.put(`/api/admin/users/${userId}/role`, { role_id: roleId });
      toast.success('Role updated');
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to update role'));
    }
  };

  const getRoleBadge = (roleName) => {
    const r = (roleName || '').toLowerCase();
    if (r.includes('super')) return { bg: 'bg-red-100', text: 'text-red-700' };
    if (r.includes('finance')) return { bg: 'bg-emerald-100', text: 'text-emerald-700' };
    if (r.includes('admin')) return { bg: 'bg-blue-100', text: 'text-blue-700' };
    if (r.includes('affiliated')) return { bg: 'bg-violet-100', text: 'text-violet-700' };
    if (r.includes('member')) return { bg: 'bg-gray-100', text: 'text-gray-700' };
    return { bg: 'bg-slate-100', text: 'text-slate-700' };
  };

  const getStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'active') return { bg: 'bg-green-100', text: 'text-green-700' };
    if (s === 'pending') return { bg: 'bg-yellow-100', text: 'text-yellow-700' };
    if (s === 'inactive') return { bg: 'bg-gray-100', text: 'text-gray-700' };
    return { bg: 'bg-gray-100', text: 'text-gray-700' };
  };

  const permissionCategories = [
    { key: 'target_members', label: 'Target Members', actions: ['view', 'create', 'edit', 'delete', 'assign', 'follow_up', 'bulk_import'] },
    { key: 'members', label: 'Members', actions: ['view', 'edit', 'delete', 'validate', 'restrict'] },
    { key: 'payments', label: 'Payments', actions: ['view', 'confirm', 'refund'] },
    { key: 'invoices', label: 'Invoices', actions: ['view', 'generate', 'edit'] },
    { key: 'tiers', label: 'Tiers', actions: ['view', 'edit', 'create', 'delete'] },
    { key: 'points', label: 'Points', actions: ['view', 'edit', 'grant'] },
    { key: 'team', label: 'Team', actions: ['view', 'invite', 'edit', 'delete'] },
    { key: 'templates', label: 'Templates', actions: ['view', 'create', 'edit', 'delete'] },
    { key: 'reports', label: 'Reports', actions: ['view', 'export'] },
    { key: 'settings', label: 'Settings', actions: ['view', 'edit'] }
  ];

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-500">View and manage admin portal users</p>
        </div>
        {canInvite && (
          <Button
            onClick={() => setShowAddUserDialog(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="add-user-btn"
          >
            <UserPlus size={16} className="mr-2" />
            Add User
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Users</p>
                <p className="text-2xl font-bold">{users.length}</p>
              </div>
              <Users size={24} className="text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Super Admins</p>
                <p className="text-2xl font-bold">{users.filter(u => (u.role_name || u.role || '').toLowerCase().includes('super')).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active</p>
                <p className="text-2xl font-bold">{users.filter(u => u.status === 'active').length}</p>
              </div>
              <CheckCircle size={24} className="text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold">{users.filter(u => u.status === 'pending').length}</p>
              </div>
              <Clock size={24} className="text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-4 items-center">
        <Tabs value={userType} onValueChange={setUserType}>
          <TabsList>
            <TabsTrigger value="all">All Users</TabsTrigger>
            <TabsTrigger value="admin">Admin Users</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 max-w-sm">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-700">User</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-700">Email</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-700">Type</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-700">Role</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-700">Department</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-700">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-700">Last Login</th>
                  {canEdit && (
                    <th className="text-right px-6 py-4 text-sm font-medium text-gray-700">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.map((u) => {
                  const isCurrentUser = u.user_id === admin?.admin_id;
                  return (
                    <tr key={u.user_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                            {u.user_type === 'admin' ? (
                              <UserCheck size={16} className="text-slate-600" />
                            ) : (
                              <User size={16} className="text-slate-600" />
                            )}
                          </div>
                          <span className="font-medium">
                            {u.full_name || 'N/A'}
                            {isCurrentUser && <span className="text-xs text-blue-600 ml-2">(You)</span>}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">{u.email}</td>
                      <td className="px-6 py-4">
                        <Badge variant="outline">
                          {u.user_type === 'admin' ? 'Admin Portal' : 'Member Portal'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        {u.user_type === 'admin' && canEdit ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm" className="gap-1">
                                {u.role_name || u.role || 'N/A'}
                                <ChevronDown size={14} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                              {availableAdminRoles.map((r) => (
                                  <DropdownMenuItem
                                    key={r.role_id}
                                    onClick={() => handleRoleChange(u.user_id, r.role_id)}
                                  >
                                    {r.name}
                                  </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                          <Badge className={getRoleBadge(u.role_name || u.role).bg + ' ' + getRoleBadge(u.role_name || u.role).text}>
                            {u.role_name || u.role || 'N/A'}
                          </Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{u.department || '-'}</td>
                      <td className="px-6 py-4">
                        <Badge className={getStatusBadge(u.status).bg + ' ' + getStatusBadge(u.status).text}>
                          {u.status || 'N/A'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {u.last_login ? new Date(u.last_login).toLocaleDateString() : '-'}
                      </td>
                      {canEdit && (
                        <td className="px-6 py-4 text-right">
                          {!isCurrentUser && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical size={16} />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditDialog(u)}>
                                  <Edit2 size={14} className="mr-2" /> Edit Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openPermissionsDialog(u)}>
                                  <Key size={14} className="mr-2" /> Manage Permissions
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {u.status === 'active' ? (
                                  <DropdownMenuItem
                                    className="text-red-600"
                                    onClick={() => handleDeactivate(u.user_id)}
                                  >
                                    <XCircle size={14} className="mr-2" /> Deactivate
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem
                                    className="text-green-600"
                                    onClick={() => handleReactivate(u.user_id)}
                                  >
                                    <CheckCircle size={14} className="mr-2" /> Reactivate
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {!loading && users.length === 0 && (
            <div className="p-8 text-center text-gray-500">No users found</div>
          )}
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <UserPlus size={20} className="mr-2 text-blue-600" />
              Add User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={addUserForm.email}
                onChange={(e) => setAddUserForm({ ...addUserForm, email: e.target.value })}
                placeholder="user@company.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Full Name *</Label>
              <Input
                value={addUserForm.full_name}
                onChange={(e) => setAddUserForm({ ...addUserForm, full_name: e.target.value })}
                placeholder="John Doe"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Role *</Label>
              <select
                value={addUserForm.role}
                onChange={(e) => setAddUserForm({ ...addUserForm, role: e.target.value })}
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                {availableAdminRoles.map((role) => (
                  <option key={role.role_id} value={role.role_key || role.name}>
                    {role.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Department</Label>
              <Input
                value={addUserForm.department}
                onChange={(e) => setAddUserForm({ ...addUserForm, department: e.target.value })}
                placeholder="e.g., Operations, Sales"
                className="mt-1"
              />
            </div>

            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <p>An email will be sent to the user to verify their email and set their password.</p>
            </div>

            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowAddUserDialog(false)}>Cancel</Button>
              <Button onClick={handleAddUser} className="bg-blue-600 hover:bg-blue-700 text-white">
                <Mail size={16} className="mr-2" />
                Send Invitation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Full Name</Label>
              <Input
                value={editForm.full_name}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Department</Label>
              <Input
                value={editForm.department}
                onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                className="mt-1"
              />
            </div>
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button onClick={handleUpdate} className="bg-blue-600 hover:bg-blue-700 text-white">
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Key size={20} className="mr-2 text-purple-600" />
              Manage Permissions - {selectedUser?.full_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Configure what this user can access and modify.
            </p>

            <div className="space-y-4">
              {permissionCategories.map((category) => (
                <div key={category.key} className="border rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">{category.label}</h4>
                  <div className="flex flex-wrap gap-2">
                    {category.actions.map((action) => {
                      const isEnabled = permissions[category.key]?.includes(action);
                      return (
                        <button
                          key={action}
                          type="button"
                          onClick={() => {
                            const current = permissions[category.key] || [];
                            if (isEnabled) {
                              setPermissions({
                                ...permissions,
                                [category.key]: current.filter(a => a !== action)
                              });
                            } else {
                              setPermissions({
                                ...permissions,
                                [category.key]: [...current, action]
                              });
                            }
                          }}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                            isEnabled
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {action}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowPermissionsDialog(false)}>Cancel</Button>
              <Button onClick={handleUpdatePermissions} className="bg-purple-600 hover:bg-purple-700 text-white">
                <Key size={16} className="mr-2" />
                Update Permissions
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
